import type { BimWorkOrderPriceData, ContractStatusEnum, WorkOrderPriceData, WorkOrderSeasonData } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { getNumberFormatter } from 'pages/Project/Common/utils';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import { WorkOrderPriceVolumeEdit } from '../../WorkOrderPriceVolumeEdit';

import {
    getSeasonCompletedCostKey,
    getSeasonCompletedVolumeKey,
    getSeasonCostKey,
    getSeasonPlanVolumeTermsKey,
    getSeasonReminderCostKey,
    getSeasonReminderVolumeKey,
    getSeasonVolumeKey,
    showNotNull,
    sortLinkedActs,
} from './helpers';

interface IUseSeasonColumnsProps {
    onChange: (reloadRowsIds?: number[] | null) => void;
    actsColumns: ((ColDef<WorkOrderPriceData> | ColGroupDef<WorkOrderPriceData>) & {
        id?: number;
        seasonId?: number;
    })[];
    isDisabledByProjectStatus: boolean;
    workOrderStatus: WorkOrderStatusEnum | undefined;
    contractStatus: ContractStatusEnum | undefined;
    hasInProgress?: boolean;
    costRounding: number;
    seasons: WorkOrderSeasonData[];
}

interface ICellRendererProps {
    id: number;
    key: string;
    statusContract: ContractStatusEnum | undefined;
    disabledByProjectStatus: boolean;
    toolTipValueKey?: string | null;
}

export const useSeasonsColumns = (props: IUseSeasonColumnsProps) => {
    const {
        workOrderStatus,
        onChange,
        actsColumns,
        contractStatus,
        isDisabledByProjectStatus,
        hasInProgress,
        costRounding,
        seasons,
    } = props;

    const withFactAndRemainderColumns =
        workOrderStatus &&
        ([WorkOrderStatusEnum.InProgress, WorkOrderStatusEnum.Completed].includes(workOrderStatus) ||
            (workOrderStatus === WorkOrderStatusEnum.Delayed && hasInProgress));

    const cellRenderer = useCallback(
        ({ id, key, statusContract, toolTipValueKey = null, disabledByProjectStatus }: ICellRendererProps) =>
            // eslint-disable-next-line react/display-name
            ({ data }: { data: WorkOrderPriceData }) => {
                if (!data) {
                    return '';
                }

                const isDisabled = data.isExcludedPrice || disabledByProjectStatus;

                return (
                    <WorkOrderPriceVolumeEdit
                        workOrderStatus={workOrderStatus}
                        workOrderSeasonId={id}
                        workOrderPriceId={data.id}
                        defaultValue={data[key as keyof WorkOrderPriceData]}
                        onChange={onChange}
                        type={data.type}
                        contractStatus={statusContract}
                        toolTipValue={data[toolTipValueKey as keyof WorkOrderPriceData]}
                        isDisabled={isDisabled}
                    />
                );
            },
        [onChange, workOrderStatus],
    );

    return useMemo(
        () =>
            seasons.reduce((acc: (ColDef | ColGroupDef)[], { id, year }, index: number) => {
                const isOddSeason = !(index % 2);

                const cellClass = isOddSeason ? 'oddSeason' : 'evenSeason';

                const seasonPlanColumn: ColDef | ColGroupDef = {
                    headerName: `Сезон ${year}${withFactAndRemainderColumns ? ', План' : ''}`,
                    marryChildren: true,
                    headerClass: `${cellClass} flexStart`,
                    children: [
                        {
                            headerName: 'Объем',
                            cellClass: `ag-right-aligned-cell ${cellClass}`,
                            headerClass: cellClass,
                            width: 112,
                            cellRenderer:
                                workOrderStatus === WorkOrderStatusEnum.OnFormation
                                    ? cellRenderer({
                                          id,
                                          key: getSeasonVolumeKey(id),
                                          statusContract: contractStatus,
                                          toolTipValueKey: getSeasonPlanVolumeTermsKey(id),
                                          disabledByProjectStatus: isDisabledByProjectStatus,
                                      })
                                    : ({ data }: { data: BimWorkOrderPriceData }) =>
                                          showNotNull((data as any)?.[getSeasonVolumeKey(id)]),
                        },
                        {
                            headerName: 'Стоимость, руб. без НДС',
                            field: getSeasonCostKey(id),
                            cellClass: `ag-right-aligned-cell ${cellClass}`,
                            headerClass: cellClass,
                            width: 130,
                            valueFormatter: getNumberFormatter(costRounding),
                        },
                    ],
                };

                const factAndRemainderColumns = withFactAndRemainderColumns
                    ? [
                          {
                              headerName: `Сезон ${year}, Выполнено`,
                              marryChildren: true,
                              headerClass: `${cellClass} flexStart`,
                              children: [
                                  {
                                      headerName: 'Объем',
                                      cellClass: `ag-right-aligned-cell ${cellClass}`,
                                      headerClass: cellClass,
                                      width: 112,
                                      valueGetter: ({ data }: { data: BimWorkOrderPriceData }) =>
                                          showNotNull((data as any)?.[getSeasonCompletedVolumeKey(id)]),
                                  },
                                  {
                                      headerName: 'Стоимость, руб. без НДС',
                                      cellClass: `ag-right-aligned-cell ${cellClass}`,
                                      headerClass: cellClass,
                                      field: getSeasonCompletedCostKey(id),
                                      width: 130,
                                      valueFormatter: getNumberFormatter(costRounding),
                                  },
                              ],
                          },
                          {
                              headerName: `Сезон ${year}, Остаток`,
                              marryChildren: true,
                              headerClass: `${cellClass} flexStart`,
                              children: [
                                  {
                                      headerName: 'Объем',
                                      cellClass: `ag-right-aligned-cell ${cellClass}`,
                                      headerClass: cellClass,
                                      width: 112,
                                      valueGetter: ({ data }: { data: WorkOrderPriceData }) =>
                                          showNotNull((data as any)?.[getSeasonReminderVolumeKey(id)]),
                                  },
                                  {
                                      headerName: 'Стоимость, руб. без НДС',
                                      cellClass: `ag-right-aligned-cell ${cellClass}`,
                                      headerClass: cellClass,
                                      field: getSeasonReminderCostKey(id),
                                      width: 130,
                                      valueFormatter: getNumberFormatter(costRounding),
                                  },
                              ],
                          },
                      ]
                    : [];

                const linkedActs = sortLinkedActs(actsColumns.filter((a) => a.seasonId === id));

                return acc.concat(seasonPlanColumn, factAndRemainderColumns, linkedActs);
            }, []),
        [
            actsColumns,
            cellRenderer,
            contractStatus,
            costRounding,
            isDisabledByProjectStatus,
            seasons,
            withFactAndRemainderColumns,
            workOrderStatus,
        ],
    );
};
